import { useEffect, useState } from "react";
import { useTheme } from "../../../components/hooks/useTheme";
import { GridColDef } from "@mui/x-data-grid";
import { CustomDataGrid } from "../../../components/common/CustomDataGrid";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import baseURL from "../../../configs/api";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import { PaymentDetailsPopup } from "./PaymentDetailsPopup";
import { bookingsStatusColorCode } from "../../../utils/statusColorCode";
import { Link } from "react-router-dom";
import moment from "moment";
import useGeneral from "../../../components/hooks/useGeneral";

const filterItems = [
    {
        id: 0,
        label: "Customer Name",
    },
    {
        id: 1,
        label: "Booking Number",
    },
    {
        id: 2,
        label: "Reference",
    },
    {
        id: 3,
        label: "Vehicle Class Name",
    },
    {
        id: 4,
        label: "Pickup Location Name",
    },
]

export const AllPayments = () => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [quotesFilterValue, setQuotesFilterValue] = useState(0);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [particularQuotes, setParticularQuotes] = useState<any>();
    const [totalLength, setTotalLength] = useState<number>(0);
    const [selectedFilter, setSelectedFilter] = useState<string>(filterItems[0].label);
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const columnFilterOpen = Boolean(columnFilterAnchorEl);
    const initialColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "#",
            width: 100,
            editable: false,
            sortable: false
        },
        {
            field: "bookingNumber",
            headerName: "Reservation",
            width: 120,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Link to={`/carrental/reservations/editreservation?id=${params?.row.reservationId}`}
                        style={{
                            color: "var(--app-link-color)",
                            textDecoration: "underline",
                        }}
                    >
                        {params?.value}
                    </Link>
                );
            },
        },
        {
            field: "customerName",
            headerName: "Customer Name",
            width: 120,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <span
                        onClick={() => {
                            setShowPopup(true);
                            setParticularQuotes(params?.row);
                        }}
                        style={{ color: "var(--app-link-color)" }}
                        className="underline cursor-pointer"
                    >
                        {params?.value}
                    </span>
                );
            },
        },
        {
            field: "paymentType",
            headerName: "Payment Type",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "paymentMethod",
            headerName: "Payment Method",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "dateofPayment",
            headerName: "Date",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "reference",
            headerName: "Reference",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "paymentStatus",
            headerName: "Payment Status",
            width: 120,
            editable: false,
            sortable: false
        },
        {
            field: "pickupDate",
            headerName: "Pickup Date",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "returnDate",
            headerName: "Return Date",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "pickupLocationName",
            headerName: "Pickup Location",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "vehicleClassName",
            headerName: "Vehicle Class",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "localAddress",
            headerName: "Local Address",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "totalPrice",
            headerName: "Total Price",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "totalRevenue",
            headerName: "Total Revenue",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "totalPaid",
            headerName: "Total Paid",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "totalRefunded",
            headerName: "Total Refunded",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "outstandingBalance",
            headerName: "Outstanding Balance",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                let color = {
                    colorBg: "#000000",
                    colorText: "#000000"
                };
                color = bookingsStatusColorCode?.find((color: any) => color?.label === params?.value?.trim()?.toLowerCase()) || {
                    colorBg: "#000000",
                    colorText: "#000000"
                };

                return (
                    <div className="flex items-end h-full">
                        <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}>
                            {params.value}
                        </span>
                    </div>
                );
            }
        },
        {
            field: "branchName",
            headerName: "Branch",
            width: 120,
            editable: false,
            sortable: false,
        },
        {
            field: "walkInCustomer",
            headerName: "Walk in Customer?",
            width: 120,
            editable: false,
            sortable: false,
        },
    ];
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnFilterAnchorEl(event.currentTarget);
    };
    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const toggleColumn = (headerName: string) => {
        setSelectedColumns(prev =>
            prev?.includes(headerName) ? prev?.filter(col => col !== headerName) : [...prev, headerName]
        );
    };

    useEffect(() => {
        const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
        setColumns(filterCol);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns])

    const toggleAll = () => {
        if (selectedColumns?.length === initialColumns?.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(initialColumns?.map(col => col?.headerName));
        }
    };

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false);
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {
        setLoading(true);
        fetch(`${baseURL}payment/GetPaymentsPaging/${newPaginationModel.page}/${newPaginationModel.pageSize}`, {
            method: 'GET',
            headers: {
                'X-Tenant-ID': tenantId?.toString(),
                'Content-Type': 'application/json'
            }
        })
            .then((res) => res.json())
            .then((data) => {
                const result = data.payments.map((item: any) => {
                    return {
                        ...item,
                        dateofPayment: item.dateofPayment
                            ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                            : "",
                        pickupDate: item.pickupDate
                            ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                            : "",
                        returnDate: item.returnDate
                            ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                            : "",
                        walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                        amount: "$" + item.amount?.toFixed(2),
                        totalPrice: "$" + item.totalPrice?.toFixed(2),
                        totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                        totalPaid: "$" + item.totalPaid?.toFixed(2),
                        totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                        outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                    };
                });

                setSearchTableData(result);
                setTotalLength(data.count);
                setLoading(false);
                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
                setSearchTableData([]);
                setLoading(false);
            });
    }

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    pageSize: paginationModel?.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            apiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])


    const apiCall = (newPaginationModel: any) => {
        let filterData: any[] = [];
        setLoading(true);
        switch (quotesFilterValue) {
            case 0:
                fetch(`${baseURL}payment/GetPaymentsPagingSearchCustomerName?customerName=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-ID': tenantId?.toString(),
                        'Content-Type': 'application/json'
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.payments.map((item: any) => {
                            return {
                                ...item,
                                dateofPayment: item.dateofPayment
                                    ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                                    : "",
                                pickupDate: item.pickupDate
                                    ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                returnDate: item.returnDate
                                    ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                                amount: "$" + item.amount?.toFixed(2),
                                totalPrice: "$" + item.totalPrice?.toFixed(2),
                                totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                                totalPaid: "$" + item.totalPaid?.toFixed(2),
                                totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                                outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    });
                break;
            case 1:
                fetch(`${baseURL}payment/GetPaymentsPagingSearchBookingNumber?bookingNumber=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-ID': tenantId?.toString(),
                        'Content-Type': 'application/json'
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.payments.map((item: any) => {
                            return {
                                ...item,
                                dateofPayment: item.dateofPayment
                                    ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                                    : "",
                                pickupDate: item.pickupDate
                                    ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                returnDate: item.returnDate
                                    ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                                amount: "$" + item.amount?.toFixed(2),
                                totalPrice: "$" + item.totalPrice?.toFixed(2),
                                totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                                totalPaid: "$" + item.totalPaid?.toFixed(2),
                                totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                                outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    })

                break;
            case 2:
                fetch(`${baseURL}payment/GetPaymentsPagingSearchReference?reference=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-ID': tenantId?.toString(),
                        'Content-Type': 'application/json'
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.payments.map((item: any) => {
                            return {
                                ...item,
                                dateofPayment: item.dateofPayment
                                    ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                                    : "",
                                pickupDate: item.pickupDate
                                    ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                returnDate: item.returnDate
                                    ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                                amount: "$" + item.amount?.toFixed(2),
                                totalPrice: "$" + item.totalPrice?.toFixed(2),
                                totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                                totalPaid: "$" + item.totalPaid?.toFixed(2),
                                totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                                outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    });
                break;
            case 3:
                fetch(`${baseURL}payment/GetPaymentsPagingSearchVehicleClassName?vehicleClassName=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-ID': tenantId?.toString(),
                        'Content-Type': 'application/json'
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.payments.map((item: any) => {
                            return {
                                ...item,
                                dateofPayment: item.dateofPayment
                                    ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                                    : "",
                                pickupDate: item.pickupDate
                                    ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                returnDate: item.returnDate
                                    ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                                amount: "$" + item.amount?.toFixed(2),
                                totalPrice: "$" + item.totalPrice?.toFixed(2),
                                totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                                totalPaid: "$" + item.totalPaid?.toFixed(2),
                                totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                                outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    })

                break;
            case 4:
                fetch(`${baseURL}payment/GetPaymentsPagingSearchPickupLocationName?pickuplocationName=${searchValue}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    method: 'GET',
                    headers: {
                        'X-Tenant-ID': tenantId?.toString(),
                        'Content-Type': 'application/json'
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.payments.map((item: any) => {
                            return {
                                ...item,
                                dateofPayment: item.dateofPayment
                                    ? moment(new Date(item.dateofPayment)).format(dateFormat + "hh:mm a")
                                    : "",
                                pickupDate: item.pickupDate
                                    ? moment(new Date(item.pickupDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                returnDate: item.returnDate
                                    ? moment(new Date(item.returnDate)).format(dateFormat + "hh:mm a")
                                    : "",
                                walkInCustomer: item.walkInCustomer ? "Yes" : "No",
                                amount: "$" + item.amount?.toFixed(2),
                                totalPrice: "$" + item.totalPrice?.toFixed(2),
                                totalRevenue: "$" + item.totalRevenue?.toFixed(2),
                                totalPaid: "$" + item.totalPaid?.toFixed(2),
                                totalRefunded: "$" + item.totalRefunded?.toFixed(2),
                                outstandingBalance: "$" + item.outstandingBalance?.toFixed(2),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    })

                break;
        }
    }

    return (
        <>
            <div className="w-full mt-2">
                <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
                    <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
                        <div className="flex items-center gap-1.5">

                            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Payments</h2>

                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                    aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={columnFilterOpen ? 'true' : undefined}
                                    onClick={handleColumnFilterClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>Columns</span>
                                        <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                                            filter_alt
                                        </span>
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={columnFilterAnchorEl}
                                    open={columnFilterOpen}
                                    onClose={handleColumnFilterClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                                maxHeight: 300,
                                                backgroundColor: themeStyles.topBar.bg
                                            }
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox cursor-pointer"
                                            checked={selectedColumns.length === initialColumns.length ? true : false}
                                            onClick={toggleAll}
                                        />
                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium text-[12px]`}>Select All</span>
                                    </div>
                                    <Divider className="w-full" />
                                    {
                                        initialColumns?.map((column, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{
                                                        fontSize: "12px",
                                                        marginBlock: "0px",
                                                        "&: hover": {
                                                            cursor: "default"
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-checkbox cursor-pointer"
                                                            checked={selectedColumns.includes(column?.headerName)}
                                                            onChange={() => toggleColumn(column?.headerName || "")}
                                                        />
                                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium`}>{column?.headerName}</span>
                                                    </div>

                                                </MenuItem>
                                                <Divider className="w-full" />
                                            </>

                                        ))
                                    }
                                </Menu>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                    aria-controls={open ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>{selectedFilter}</span>
                                        {open ? <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                            keyboard_arrow_up
                                        </span> : <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                            keyboard_arrow_down
                                        </span>}
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 200,
                                                backgroundColor: themeStyles.topBar.bg
                                            }
                                        }
                                    }}
                                >
                                    {
                                        filterItems?.map((filter, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                    onClick={() => {
                                                        setSelectedFilter(filter.label)
                                                        setQuotesFilterValue(filter.id)
                                                        setAnchorEl(null);

                                                    }}>
                                                    <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>{filter.label}</span>
                                                </MenuItem>
                                                <Divider className="w-full" />
                                            </>

                                        ))
                                    }
                                </Menu>
                            </div>
                            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>
                                <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent`}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <CustomDataGrid
                        filteredRows={searchTableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: themeStyles.table.headerBg,
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>

            <PaymentDetailsPopup
                particularQuotes={particularQuotes}
                showPopup={showPopup}
                hidePopup={() => setShowPopup(false)}
                deleteQuote={() => {
                    const newPaginationModel = {
                        page: 0,
                        pageSize: paginationModel.pageSize,
                    };

                    setPaginationModel(newPaginationModel);
                    setShowPopup(false);
                }}
            />
        </>

    )
}